import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/components/site/RecognitionPress.css";

const LOGO_BASE = "/assets/press";

const PUBLICATIONS = [
  {
    key: "ad",
    name: "Architectural Digest",
    logo: `${LOGO_BASE}/ad-logo.png`,
    href: "https://www.architecturaldigest.in/story/this-multi-generational-bengaluru-home-basks-in-the-afterglow-of-warm-memories-the-hippodrome-setting/",
    sizeClass: "is-ad",
  },
  {
    key: "elle",
    name: "Elle Decor",
    logo: `${LOGO_BASE}/elle-decor-logo.png`,
    href: "https://elledecor.in/bengaluru-home-designed-the-hippodrome-setting-ode-clean-lines/",
    sizeClass: "is-elle",
  },
  {
    key: "gg",
    name: "G&G",
    logo: `${LOGO_BASE}/gg-magazine-logo.png`,
    href: "https://issuu.com/casaliving_it/docs/tender_peach_fuzz_january_2024",
    sizeClass: "is-gg",
  },
  {
    key: "gh",
    name: "Good Homes",
    logo: `${LOGO_BASE}/goodhomes-logo.png`,
    href: "https://www.goodhomes.co.in/home-decor/tie-untie-a-home-where-every-space-resonates-with-each-other-8988.html",
    sizeClass: "is-gh",
  },
  {
    key: "tad",
    name: "The Architect's Diary",
    logo: `${LOGO_BASE}/architects-diary-logo.png`,
    href: "https://thearchitectsdiary.com/the-glass-front-facade-articulated-the-benefits-of-natural-light-the-hippodrome-setting/",
    sizeClass: "is-tad",
  },
];

const TICKER = [
  {
    name: "Architectural Digest",
    title: "This multigenerational Bengaluru home basks in warm memories",
    href: PUBLICATIONS[0].href,
  },
  {
    name: "Elle Decor",
    title: "This Bengaluru home is an ode to clean lines",
    href: PUBLICATIONS[1].href,
  },
  {
    name: "G&G",
    title: "International magazine cover feature",
    href: PUBLICATIONS[2].href,
  },
  {
    name: "Good Homes",
    title: "A home where every space resonates with each other",
    href: PUBLICATIONS[3].href,
  },
  {
    name: "The Architect's Diary",
    title: "A glass-front facade shaped by natural light",
    href: PUBLICATIONS[4].href,
  },
];

export default function RecognitionPress({ id = "press-section" }) {
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef(null);

  // Scroll-triggered fade for intro + logos
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;
    const targets = root.querySelectorAll("[data-press-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [reducedMotion]);

  // For seamless loop, duplicate the ticker list twice in the track
  const tickerLoop = [...TICKER, ...TICKER];

  return (
    <section
      ref={sectionRef}
      id={id}
      className="press"
      data-testid="press-section"
      aria-label="Press and publications"
    >
      <div className="press__paper" aria-hidden="true" />

      <div className="press__inner">
        <div className="press__intro" data-press-reveal data-testid="press-intro">
          <span className="press__eyebrow">Press &amp; Publications</span>
          <h2 className="press__headline">Stories carried by leading design journals</h2>
          <p className="press__subtext">
            Our homes have been featured across Indian and international
            design media.
          </p>
        </div>

        {/* Static masthead-style logo row */}
        <div
          className="press__logos"
          data-press-reveal
          data-testid="press-logos"
        >
          {PUBLICATIONS.map((p, i) => (
            <a
              key={p.key}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`press__logo press__logo--${p.sizeClass}`}
              style={{ transitionDelay: `${120 + i * 80}ms` }}
              aria-label={`${p.name} — read article`}
              data-testid={`press-logo-${p.key}`}
            >
              <img src={p.logo} alt={p.name} loading="lazy" />
            </a>
          ))}
        </div>
      </div>

      {/* Marquee — slow editorial ticker */}
      <div className="press__marquee-frame" data-testid="publications-section">
        <span className="press__rule" aria-hidden="true" />
        <div
          className={[
            "press__marquee",
            reducedMotion ? "is-static" : "",
          ].join(" ")}
          data-testid="press-marquee"
        >
          {reducedMotion ? (
            <ul className="press__marquee-static">
              {TICKER.map((t, i) => (
                <li key={i}>
                  <a
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="press__ticker-item"
                  >
                    <span className="press__ticker-pub">{t.name}</span>
                    <span className="press__ticker-sep" aria-hidden="true">/</span>
                    <span className="press__ticker-title">{t.title}</span>
                    <span className="press__ticker-arrow" aria-hidden="true">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="press__marquee-track" aria-hidden="false">
              {tickerLoop.map((t, i) => (
                <a
                  key={i}
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="press__ticker-item"
                  aria-hidden={i >= TICKER.length ? "true" : undefined}
                  tabIndex={i >= TICKER.length ? -1 : 0}
                >
                  <span className="press__ticker-pub">{t.name}</span>
                  <span className="press__ticker-sep" aria-hidden="true">/</span>
                  <span className="press__ticker-title">{t.title}</span>
                  <span className="press__ticker-arrow" aria-hidden="true">↗</span>
                  <span className="press__ticker-dot" aria-hidden="true" />
                </a>
              ))}
            </div>
          )}
        </div>
        <span className="press__rule" aria-hidden="true" />
      </div>

      <div className="press__viewall" data-press-reveal data-testid="press-viewall-wrap">
        <Link
          to="/press"
          className="press__viewall-link"
          data-testid="press-viewall-link"
          aria-label="View all press"
        >
          <span>View all press</span>
          <span className="press__viewall-arrow" aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
